import React, { useEffect, useState } from "react";
import { useIsFocused } from "@react-navigation/native";
import { _goToAddNote } from "routers/helper";

import { View, SafeAreaView } from "react-native";
import {
  Input,
  Error,
  Empty,
  Button,
  Header,
  Loading,
  NoteBox,
  AddButtonFloat,
  AnimatedInfinityList,
} from "components";
import Filters, { NotesFiltersT } from "./Filters";
import SelectedFiltersView from "screens/components/SelectedFiltersView";

import { colors, sizes, strings } from "assets/constants";
import { mq, conditions, liveSearchInput } from "utils";
import { filtersItems } from "assets/data";
import { useRecoilValue } from "recoil";
import { authAtom } from "stores";
import { useChmod, useFlag } from "hooks";
import css from "styles";

import { useNotes } from "clients/apis";

let timer: ReturnType<typeof setTimeout>;

export default function Notes() {
  const { chmod } = useChmod();
  const isFocused = useIsFocused();

  const {
    company: { color },
  } = useRecoilValue(authAtom);
  const { flg: typing, on: startTyping, off: stopTyping } = useFlag();
  const { flg: filters, on: showFilters, off: hideFilters } = useFlag();

  const [search, setSearch] = useState("");
  const [qs, setQs] = useState<NotesFiltersT>({});
  const {
    data,
    error,
    isError,
    refetch,
    isLoading,
    isRefetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotes({ title: search, customerId: qs.customer?.id });

  const _onApplyFilters = (newFilters: NotesFiltersT) => {
    hideFilters();
    setQs(newFilters);
  };

  useEffect(() => {
    isFocused && refetch();
  }, [isFocused, refetch]);

  const _renderItem = (items: []) => {
    return (
      <View style={{ ...css.responsiveListContainer }}>
        {items.map((note: NoteT, key: number) => (
          <View key={String(key)} style={css.responsiveBoxContainer}>
            <NoteBox note={note} />
          </View>
        ))}
      </View>
    );
  };

  const _renderContent = () => {
    if (isLoading || typing) {
      return <Loading style={{ marginTop: mq(20) }} />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    if (data?.pages?.[0]?.data?.data?.length === 0) {
      return <Empty style={{ marginTop: mq(20) }} message={strings.no_note} />;
    }

    return (
      <AnimatedInfinityList
        responseData={data}
        onRefresh={refetch}
        renderItemList={_renderItem}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        paddingBottom={70}
        paddingTop={sizes.space}
      />
    );
  };

  return (
    <>
      <Header title={strings.notes} />
      <>
        <SafeAreaView style={{ ...css.container, ...css.screenBg }}>
          <View
            style={{
              ...css.paddingH,
              ...css.rowCenterAlign,
              marginTop: sizes.space,
            }}
          >
            <Input
              onChangeText={(text) =>
                liveSearchInput(
                  text,
                  search,
                  setSearch,
                  timer,
                  startTyping,
                  stopTyping
                )
              }
              placeholder={strings.search}
              icon={{ name: "search" }}
              hideError
              leftComponent={
                <Button
                  onPress={showFilters}
                  left={4}
                  square={38}
                  radius={sizes.radius.lg}
                  icon={{ name: "sliders", color, size: 24 }}
                  bgColor={colors.white}
                />
              }
            />
          </View>

          {conditions.isFiltered(qs) && (
            <View style={{ paddingTop: sizes.space }}>
              <SelectedFiltersView
                filters={qs}
                onChange={setQs}
                filterItems={filtersItems.notes}
              />
            </View>
          )}

          {_renderContent()}

          <Filters
            initials={qs}
            visible={filters}
            hide={hideFilters}
            apply={_onApplyFilters}
          />
        </SafeAreaView>
        {chmod("note", "POST") && <AddButtonFloat onPress={_goToAddNote} />}
      </>
    </>
  );
}
